import useUserStore from "../store/userStore";

const SITE_BASE = (process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(/\/$/, "");
const API_BASE = SITE_BASE.endsWith("/api") ? SITE_BASE : `${SITE_BASE}/api`;
const CONTRIBUTIONS_BASE = `${API_BASE}/place-contributions/v1`;

export interface ContributionTaxonomyOption {
  id: number;
  name: string;
}

export interface PlaceContributionDraft {
  name: string;
  city: number;
  categories: number[];
  tags?: number[];
  address?: string;
  description?: string;
  phone?: string;
  website_url?: string;
  instagram_url?: string;
  latitude?: number;
  longitude?: number;
  google_maps_url?: string;
  opening_schedule?: OpeningScheduleDay[];
  timezone: "Asia/Tehran";
}

export interface OpeningScheduleDay {
  day_of_week: number;
  state: "intervals";
  intervals: Array<{
    opens_at: string;
    closes_at: string;
    crosses_midnight: boolean;
  }>;
}

export interface PlaceContributionSubmission {
  submission_id: string;
  id?: string;
  kind?: string;
  status: "draft" | "pending" | "accepted" | "published" | "rejected" | string;
  validation_state?: "valid" | "invalid" | string;
  warnings?: string[];
  rejection_reason?: string | null;
  name?: string;
  city?: number | { id: number; name?: string };
  categories?: Array<number | { id: number; name?: string }>;
  address?: string;
  description?: string;
  phone?: string;
  google_maps_url?: string | null;
  opening_schedule?: OpeningScheduleDay[];
  website_url?: string | null;
  instagram_url?: string | null;
  tags?: Array<number | { id: number; name?: string }>;
  images?: UploadedContributionImage[];
  created_at?: string;
  updated_at?: string;
}

export interface UploadedContributionImage {
  id: string;
  url: string;
  format: string;
  size_bytes: number;
  width: number;
  height: number;
}

export class PlaceContributionApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly fields?: Record<string, string[] | string>,
    public readonly fieldCodes?: Record<string, string[]>,
    public readonly retryAfterSeconds?: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "PlaceContributionApiError";
  }
}

const getCsrfToken = () => {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith("csrftoken="))
    ?.split("=")
    .slice(1)
    .join("=");
};

const getAuthHeaders = (): HeadersInit => {
  const token = useUserStore.getState().token;
  const csrfToken = getCsrfToken();

  return {
    ...(token ? { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` } : {}),
    ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
  };
};

const getPayload = async (response: Response) => response.json().catch(() => ({}));

const getStatusFallback = (status: number) => {
  if (status === 401) return "نشست شما منقضی شده است. لطفاً دوباره وارد شوید.";
  if (status === 403) return "اجازه انجام این عملیات را ندارید.";
  if (status === 404) return "درخواست موردنظر پیدا نشد.";
  if (status === 409) return "اطلاعات تغییر کرده است. صفحه را به‌روزرسانی و دوباره تلاش کنید.";
  if (status === 429) return "تعداد درخواست‌ها زیاد است. کمی بعد دوباره تلاش کنید.";
  if (status >= 500) return "سرویس موقتاً در دسترس نیست. لطفاً دوباره تلاش کنید.";
  return "ثبت درخواست ممکن نشد. لطفاً دوباره تلاش کنید.";
};

const getErrorMessage = (body: any, status = 0) => (
  (() => {
    const message = body?.error?.message ?? body?.message ?? body?.detail;
    if (message === "Too many active drafts.") {
      return "تعداد پیش‌نویس‌های فعال شما به حد مجاز رسیده است. ابتدا یکی از پیش‌نویس‌های قبلی باید ارسال یا بسته شود.";
    }
    return message ?? getStatusFallback(status);
  })()
);

const unwrapData = <T,>(body: any): T => (body?.data ?? body) as T;

const normalizeSubmission = (item: any): PlaceContributionSubmission => ({
  ...item,
  submission_id: item?.submission_id ?? item?.id,
  images: Array.isArray(item?.images)
    ? item.images
    : Array.isArray(item?.staged_images)
      ? item.staged_images
      : [],
});

const getRetryAfterSeconds = (response: Response, body: any) => {
  const value = response.headers.get("Retry-After")
    ?? body?.error?.retry_after_seconds
    ?? body?.error?.retry_after
    ?? body?.retry_after_seconds
    ?? body?.retry_after;
  if (value == null) return undefined;

  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds > 0) return Math.ceil(seconds);

  const retryDate = Date.parse(String(value));
  if (!Number.isNaN(retryDate)) return Math.max(1, Math.ceil((retryDate - Date.now()) / 1000));
  return undefined;
};

const createApiError = (body: any, response: Response) => new PlaceContributionApiError(
  getErrorMessage(body, response.status),
  response.status,
  body?.error?.field_errors ?? body?.error?.fields ?? body?.field_errors ?? body?.fields,
  body?.error?.field_codes ?? body?.field_codes,
  getRetryAfterSeconds(response, body),
  body?.error?.code ?? body?.code,
);

const getSubmissionList = (body: any): PlaceContributionSubmission[] => {
  const data = body?.data ?? body;
  const values = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
      ? data.results
      : Array.isArray(body?.results)
        ? body.results
        : [];
  return values.map(normalizeSubmission).filter((item: PlaceContributionSubmission) => (
    Boolean(item.submission_id && typeof item.status === "string")
  ));
};

const getTaxonomy = async (path: string, signal?: AbortSignal): Promise<ContributionTaxonomyOption[]> => {
  const response = await fetch(`${API_BASE}${path}`, { signal });
  const body = await getPayload(response);

  if (!response.ok || body?.success === false) {
    throw createApiError(body, response);
  }

  const data = body?.data ?? body;
  const values: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
      ? data.results
      : Array.isArray(body?.results)
        ? body.results
        : [];
  return values.filter((item: unknown): item is ContributionTaxonomyOption => {
    const value = item as ContributionTaxonomyOption;
    return Number.isFinite(Number(value?.id)) && typeof value?.name === "string";
  }).map((item: ContributionTaxonomyOption) => ({ ...item, id: Number(item.id) }));
};

// These are the same public taxonomy endpoints already used by the planner.
export const fetchContributionCities = (signal?: AbortSignal) => getTaxonomy("/cities/", signal);
export const fetchContributionCategories = (signal?: AbortSignal) => getTaxonomy("/categories/", signal);
export const fetchContributionTags = (signal?: AbortSignal) => getTaxonomy("/tags/", signal);

export const createPlaceContributionDraft = async (
  payload: PlaceContributionDraft,
  idempotencyKey: string,
): Promise<PlaceContributionSubmission> => {
  const response = await fetch(`${CONTRIBUTIONS_BASE}/submissions/`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  const body = await getPayload(response);

  if (!response.ok || body?.success === false) {
    throw createApiError(body, response);
  }

  return normalizeSubmission(unwrapData<PlaceContributionSubmission>(body));
};

export const updatePlaceContributionDraft = async (
  submissionId: string,
  payload: Partial<PlaceContributionDraft>,
): Promise<PlaceContributionSubmission> => {
  const response = await fetch(`${CONTRIBUTIONS_BASE}/submissions/${encodeURIComponent(submissionId)}/`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  const body = await getPayload(response);

  if (!response.ok || body?.success === false) {
    throw createApiError(body, response);
  }

  return normalizeSubmission(unwrapData<PlaceContributionSubmission>(body));
};

export const fetchPlaceContributionSubmissions = async (
  signal?: AbortSignal,
): Promise<PlaceContributionSubmission[]> => {
  const response = await fetch(`${CONTRIBUTIONS_BASE}/submissions/?status=draft`, {
    credentials: "include",
    headers: getAuthHeaders(),
    signal,
  });
  const body = await getPayload(response);

  if (!response.ok || body?.success === false) {
    throw createApiError(body, response);
  }

  return getSubmissionList(body);
};

export const deletePlaceContributionDraft = async (submissionId: string): Promise<void> => {
  const response = await fetch(`${CONTRIBUTIONS_BASE}/submissions/${encodeURIComponent(submissionId)}/`, {
    method: "DELETE",
    credentials: "include",
    headers: getAuthHeaders(),
  });

  if (response.status === 204) return;
  const body = await getPayload(response);
  if (!response.ok || body?.success === false) {
    throw createApiError(body, response);
  }
};

export const getPlaceContributionSubmission = async (
  submissionId: string,
): Promise<PlaceContributionSubmission> => {
  const response = await fetch(`${CONTRIBUTIONS_BASE}/submissions/${submissionId}/`, {
    credentials: "include",
    headers: getAuthHeaders(),
  });
  const body = await getPayload(response);

  if (!response.ok || body?.success === false) {
    throw createApiError(body, response);
  }

  return normalizeSubmission(unwrapData<PlaceContributionSubmission>(body));
};

export const submitPlaceContributionDraft = async (
  submissionId: string,
): Promise<PlaceContributionSubmission> => {
  const response = await fetch(`${CONTRIBUTIONS_BASE}/submissions/${encodeURIComponent(submissionId)}/submit/`, {
    method: "POST",
    credentials: "include",
    headers: getAuthHeaders(),
  });
  const body = await getPayload(response);

  if (!response.ok || body?.success === false) {
    throw createApiError(body, response);
  }

  return normalizeSubmission(unwrapData<PlaceContributionSubmission>(body));
};

export const uploadPlaceContributionImage = async (
  submissionId: string,
  image: File,
): Promise<UploadedContributionImage> => {
  const formData = new FormData();
  formData.append("image", image);

  const response = await fetch(`${CONTRIBUTIONS_BASE}/submissions/${encodeURIComponent(submissionId)}/images/`, {
    method: "POST",
    credentials: "include",
    headers: getAuthHeaders(),
    body: formData,
  });
  const body = await getPayload(response);

  if (!response.ok || body?.success === false) {
    throw createApiError(body, response);
  }

  return unwrapData<UploadedContributionImage>(body);
};
